import {
    AlertColor,
    Button,
    CircularProgress,
    MenuItem,
    Modal,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle } from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DropDownComboOption } from "../../component/GenericFilter";
import { Manufacturer } from "./ManufacturerAccessories";
import { renewToken } from "../../../services/AuthService";
import { OpenMode } from "../../../common/Enums";
import { GeneralDialog } from "../../component/GeneralDialog";
import { defaultPaymentTerm } from "../../../common/Constants";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    })
);

export interface ManufacturerProps {
    onClose: Function;
    paymentTerms: DropDownComboOption[];
}

export interface ManufacturerSingleHandle {
    Initialise: (mode: OpenMode, manufacturer: Manufacturer) => void;
}

export const ManufacturerSingle = React.forwardRef(
    (props: ManufacturerProps, ref: React.Ref<ManufacturerSingleHandle>) => {
        const style = { ...useStyles(), ...globalTheme() };

        const tinyScreen = useMediaQuery('(max-width:1366px)');
        const smallScreen = useMediaQuery('(max-width:1920px)');
        const normalScreen = useMediaQuery('(max-width:2560px)');

        const [open, setOpen] = React.useState(false);
        const [openMode, setOpenMode] = React.useState<OpenMode>(OpenMode.NEW);
        const [loading, setLoading] = React.useState<boolean>(false);
        const [hasError, setHasError] = React.useState(false);
        const [manufacturer, setManufacturer] = React.useState<Manufacturer>();
        const [originalManufacturer, setOriginalManufacturer] = React.useState<Manufacturer>();
        const [saved, setSaved] = React.useState<boolean>(false);

        //Dialogs and snacks
        const hasChanges = React.useRef<boolean>(false);
        const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
        const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
            open: false,
            message: "",
            subMessage: "",
            severity: undefined,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

        function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
            setSnackSettings({
                open: true,
                message: message,
                subMessage: subMessage,
                severity: colour,
                onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
            });
        }

        useImperativeHandle(ref, () => ({
            Initialise(mode: OpenMode, manufacturer: Manufacturer) {
                setOpen(true);
                setSaved(false);
                renewToken();
        
                setManufacturer(manufacturer);

                hasChanges.current = false;
                setOpenMode(mode);
            },
        }));

        function closeModal() {
            setDialogOpen(false);
            setOpen(false);
            props.onClose(saved, originalManufacturer);
        }

        function closeModalCheck(keepOpen: boolean) {
            if (hasChanges.current) {
              setDialogOpen(true);
            } else {
              setDialogOpen(false);
              setOpen(keepOpen);
              props.onClose(saved, originalManufacturer);
            }
        }

        function OnManufacturerChanged(
            propertyName: string,
            propertyValue: any,
            validationRegex?: RegExp
        ) {
            let proceed = true;
        
            if (validationRegex !== undefined) {
                proceed = validationRegex.test(propertyValue);
            }
        
            if (proceed) {
                let temp: any = { ...manufacturer };
        
                if (temp[propertyName] != propertyValue)
                {
                    hasChanges.current = true;
                    temp[propertyName] = propertyValue ?? null;
                    setManufacturer(temp);
                }
            }
        }

        function OnTradingCompanyChecked(event: React.ChangeEvent<HTMLInputElement>) {
            let temp: any = { ...manufacturer };
            temp.manufacturerType = event.target.checked ? 2 : 1;
            setManufacturer(temp);
            hasChanges.current = true;
        }
        
        function SaveManufacturer(close: boolean) {
            if (hasChanges.current)
            {
                if (openMode === OpenMode.EDIT)
                {
                    axios.put<Manufacturer>('Api/V1/Manufacturer/', manufacturer).then(
                        (result) => {
                            showSnackBar("Manufacturer saved successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setManufacturer(result.data);
                            setOriginalManufacturer(result.data);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                        showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                } 
                else
                {
                    axios.post<Manufacturer>('Api/V1/Manufacturer/', manufacturer).then(
                        (result) => {
                            showSnackBar("Manufacturer created successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setManufacturer(result.data);
                            setOriginalManufacturer(result.data);
                            setOpenMode(OpenMode.EDIT);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                }
            }
        }

        return (
            <div>
                <Modal open={open} onClose={() => closeModalCheck(true)}>
                    <div className={style.ModalMainMedium}>
                        {!loading ? (
                            <div className={style.ModalBody}>
                                <Title pageTitle="Manufacturer" />

                                <div className={style.ModalHeading}>
                                    <Typography variant={ tinyScreen ? "h6" : (smallScreen ? "h5" : "h4") }>Manufacturer</Typography>
                                </div>

                                <div className={style.ModalContent}>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Manufacturer Code"
                                            value = {manufacturer?.manufacturerCode ?? ""}
                                            onChange={(e) => OnManufacturerChanged("manufacturerCode", e.target.value.slice(0, 10))}
                                            size="small"
                                        />

                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Name"
                                            value = {manufacturer?.name ?? ""}
                                            onChange={(e) => OnManufacturerChanged("name", e.target.value.slice(0, 80))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Contact Person"
                                            value = {manufacturer?.contactPerson ?? ""}
                                            onChange={(e) => OnManufacturerChanged("contactPerson", e.target.value.slice(0, 30))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Phone 1"
                                            value = {manufacturer?.phone1 ?? ""}
                                            onChange={(e) => OnManufacturerChanged("phone1", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Phone 2"
                                            value = {manufacturer?.phone2 ?? ""}
                                            onChange={(e) => OnManufacturerChanged("phone2", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Mobile"
                                            value = {manufacturer?.mobile ?? ""}
                                            onChange={(e) => OnManufacturerChanged("mobile", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Fax 1"
                                            value = {manufacturer?.fax1 ?? ""}
                                            onChange={(e) => OnManufacturerChanged("fax1", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Fax 2"
                                            value = {manufacturer?.fax2 ?? ""}
                                            onChange={(e) => OnManufacturerChanged("fax2", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Email"
                                            value = {manufacturer?.email ?? ""}
                                            onChange={(e) => OnManufacturerChanged("email", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            id="payment-term"
                                            value={manufacturer?.paymentTerm ?? defaultPaymentTerm}
                                            onChange={(e) => OnManufacturerChanged("paymentTerm", e.target.value)}
                                            select
                                            label="Payment Term"
                                            size="small"
                                            >
                                            {props.paymentTerms.map((item) => (
                                                <MenuItem key={item.value} value={item.value}>
                                                    {item.value}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Address"
                                            value = {manufacturer?.address ?? ""}
                                            multiline
                                            rows={3}
                                            onChange={(e) => OnManufacturerChanged("address", e.target.value.slice(0, 250))}
                                            size="small"
                                        />
                                    </div>
                                </div>

                                <div className={style.ModalFooter}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={style.margin}
                                        onClick={(e) => {
                                                SaveManufacturer(false);
                                            }
                                        }
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Save
                                    </Button>
                                    <div style={{ width: `100%`, minWidth: "10" }}></div>
                                    <Button
                                        variant="contained"
                                        className={style.margin}
                                        onClick={(e) => closeModalCheck(false)}
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Close
                                    </Button>                                
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <CircularProgress color="primary" />
                            </div>
                        )}
                    </div>
                </Modal>

                <SnackBarMessage {...snackSettings} />

                <GeneralDialog
                    title={"Save Changes?"}
                    message={
                        "Changes have been made, did you want to save before closing?"
                    }
                    open={dialogOpen}
                    options={[
                        {
                        text: "Save",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => SaveManufacturer(true),
                        color: "secondary",
                        variant: "contained",
                        },
                        {
                        text: "No",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => closeModal(),
                        color: "primary",
                        variant: "contained",
                        },
                    ]}
                />

            </div>
        );


    }

);


